// modules
import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
// store
import { useStore } from 'modules/Stores';
// services
import NotificationCenterService from 'modules/NotificationCenter';
import AnalyticsService from 'modules/Analytics';
import Settings from 'modules/Settings';
import ChartSync from 'modules/ChartSync';
import FirebaseRemoteConfig from 'modules/FirebaseRemoteConfig';
import { LStorage } from 'modules/Storage';
// typings
import {
  INotification,
  INotificationAction,
  INotificationVariant,
} from 'modules/NotificationCenter/typings';
import { FEATURE_CONFIG } from 'modules/FirebaseRemoteConfig/typings';
// constants
import { EVENTS } from 'modules/Analytics/consts';
import { FETCH_NOTIFICATIONS_TIMEOUT } from 'modules/NotificationCenter/constants';
import { CounterWithExpiry } from 'modules/User/typings';
import { NotificationCenterActions } from './typings';
// components
// import BarNotification from './components/BarNotification/BarNotification';
import ModalNotification from './components/ModalNotification';

const NotificationCenterContainer: React.FC = () => {
  const { ucc } = useStore('UserStore');
  // #region Variables
  const {
    // barNotifications: barNotificationsRaw,
    modalNotifications: modalNotificationsRaw,
  } = useStore('NotificationCenterStore');
  // bar notifications
  const [barNotifications, setBarNotifications] = useState<INotification[]>([]);
  const [barNotification, setBarNotification] = useState<INotification>();
  const [barNotificationIndex, setBarNotificationIndex] = useState(0);

  // modal notifications
  const [modalNotifications, setModalNotifications] = useState<INotification[]>(
    [],
  );
  const [modalNotification, setModalNotification] = useState<INotification>();
  const [modalNotificationIndex, setModalNotificationIndex] = useState(0);

  let actions: NotificationCenterActions = {
    bar: {
      setNotifications: setBarNotifications,
      setNotification: setBarNotification,
      setNotificationIndex: setBarNotificationIndex,
      notification: barNotification,
      notifications: barNotifications,
      notificationIndex: barNotificationIndex,
    },
    modal: {
      setNotifications: setModalNotifications,
      setNotification: setModalNotification,
      setNotificationIndex: setModalNotificationIndex,
      notification: modalNotification,
      notifications: modalNotifications,
      notificationIndex: modalNotificationIndex,
    },
  };

  // update reference every time notifications are updated
  useEffect(() => {
    // eslint-disable-next-line react-hooks/exhaustive-deps
    actions = {
      bar: {
        ...actions.bar,
        notification: barNotification,
        notifications: barNotifications,
      },
      modal: {
        ...actions.modal,
        notification: modalNotification,
        notifications: modalNotifications,
      },
    };
  }, [
    modalNotification,
    modalNotifications,
    barNotification,
    barNotifications,
  ]);
  // #endregion

  // #region Data binding
  useEffect(() => {
    // load notifications
    NotificationCenterService.loadNotifications();

    // create refresh notifications job
    const refreshInterval = setInterval(() => {
      NotificationCenterService.loadNotifications();
    }, FETCH_NOTIFICATIONS_TIMEOUT);

    return () => {
      clearInterval(refreshInterval);
    };
  }, []);

  const processRawData = (
    variant: INotificationVariant,
    rawData: INotification[],
  ) => {
    // filter out notifications to be shown
    const filteredNotifications = rawData.filter((item) => {
      const current = Date.now();
      const start = new Date(item.startDate).getTime();
      const end = new Date(item.endDate).getTime();
      // check if expiry date not crossed current time
      return (
        current > start && current < end
      );
    });

    // update notifications
    actions[variant].setNotifications(filteredNotifications);

    if (filteredNotifications.length > 0) {
      // if first notification show to user
      if (!actions[variant].notification) {
        actions[variant].setNotification(filteredNotifications[0]);
        actions[variant].setNotificationIndex(0);
      } else {
        // adjust current index
        const index = filteredNotifications.findIndex(
          (item) => item.id === actions[variant].notification?.id,
        );
        if (index > -1) {
          actions[variant].setNotificationIndex(index);
        } else if (
          actions[variant].notificationIndex === filteredNotifications.length
        ) {
          // last item
          actions[variant].setNotification(
            filteredNotifications[actions[variant].notificationIndex - 1],
          );
          actions[variant].setNotificationIndex(
            actions[variant].notificationIndex - 1,
          );
        } else {
          actions[variant].setNotification(
            filteredNotifications[actions[variant].notificationIndex],
          );
          actions[variant].setNotificationIndex(
            actions[variant].notificationIndex,
          );
        }
      }
    } else {
      actions[variant].setNotifications([]);
      actions[variant].setNotification(undefined);
      actions[variant].setNotificationIndex(0);
    }
  };

  // TODO: uncomment when bar notifications supported in TV
  // useEffect(() => {
  //   processRawData('bar', barNotificationsRaw);
  // }, [barNotificationsRaw]);

  useEffect(() => {
    processRawData('modal', modalNotificationsRaw);
  }, [modalNotificationsRaw]);
  // #endregion

  // #region Pagination
  const next = (variant: INotificationVariant): void => {
    const { notification, notifications } = actions[variant];
    if (!notification) return;

    const index = notifications.findIndex(
      (item) => item.id === notification?.id,
    );
    // item found and more than one present
    if (index > -1 && notifications.length > 1) {
      let newIndex = index;
      if (index === notifications.length - 1) {
        // if last
        newIndex = 0;
      } else {
        newIndex = index + 1;
      }
      actions[variant].setNotification(notifications[newIndex]);
      actions[variant].setNotificationIndex(newIndex);
    }
  };

  const previous = (variant: INotificationVariant): void => {
    const { notification, notifications } = actions[variant];
    if (!notification) return;

    const index = notifications.findIndex(
      (item) => item.id === notification?.id,
    );
    // item found and more than one present
    if (index > -1 && notifications.length > 1) {
      let newIndex = index;
      if (index === 0) {
        // if first
        newIndex = notifications.length - 1;
      } else {
        newIndex = index - 1;
      }
      actions[variant].setNotification(notifications[newIndex]);
      actions[variant].setNotificationIndex(newIndex);
    }
  };

  // #endregion

  // #region Actions
  const handleAction = (
    action: INotificationAction,
    notification: INotification,
  ) => {
    if (action && notification) {
      if (action.action === 'close') {
        if (notification.id === 'login-notification') {
          const loginCounter: CounterWithExpiry | null = LStorage.getItem('LOGIN_COUNT');
          if (loginCounter) {
            LStorage.setItem('LOGIN_COUNT', {
              ...loginCounter,
              count: loginCounter.count + 1,
            });
          }
        }
        NotificationCenterService.delete(notification);
        if (shouldAllowAutoSync(notification)) {
          ChartSync.uploadLocalDataToCloud();
        }
        return;
      }

      const commonMixpanelProperties = {
        'Notification ID': notification.id,
        'Notification Identifier': notification.identifier || '',
        'Notification Type': notification.variant === 'modal' ? 'fullscreen' : 'warning',
        'Notification Version': notification.version || 1,
        autologin: action.autoLogin,
      } as const;

      if (action.action === 'dismiss') {
        AnalyticsService.sendEvent(
          EVENTS.CUSTOM_IN_APP_NOTIFICATION_CTA_CLICK,
          {
            ...commonMixpanelProperties,
            'CTA clicked': 'Dismiss',
          },
          true,
        );

        NotificationCenterService.markAsRead(notification);
        NotificationCenterService.delete(notification);
        return;
      }

      if (action.action === 'link' && action.link) {
        const actionType = typeof (action.link);
        if (actionType === 'string') {
          const link = action.link as string;

          // add default params
          const url = new URL(link);

          if (action.appendParams) {
            url.searchParams.append('not_id', notification.id);
            url.searchParams.append('classification', `${notification.classification}`);
            url.searchParams.append('UCC', ucc ?? '');

            if (notification.identifier) {
              url.searchParams.append('identifier', `${notification.identifier}`);
            }
            if (notification.version) {
              url.searchParams.append('version', `${notification.version}`);
            }
          }

          if (notification.dismissable) {
            NotificationCenterService.markAsRead(notification);
            NotificationCenterService.delete(notification);
          }

          AnalyticsService.sendEvent(
            EVENTS.CUSTOM_IN_APP_NOTIFICATION_CTA_CLICK,
            {
              ...commonMixpanelProperties,
              'CTA clicked': action.text,
              'CTA redirect': url.href,
            },
            true,
          );
          // open link
          window.open(url, '_blank', 'noopener, noreferrer');
        } else if (actionType === 'function') {
          // eslint-disable-next-line @typescript-eslint/ban-types
          const linkMethod = action.link as Function;
          if (linkMethod) {
            linkMethod();
          }
        }
      }
    }
  };
  // #endregion

  const shouldAllowAutoSync = (notification: INotification): boolean =>
    notification.id === 'login-notification' &&
    !Settings.getIsChartSettingsSaveEnabled() &&
    !LStorage.getItem('AUTO_SYNC_START') &&
    !LStorage.getItem('AUTO_SYNC_COMPLETE') &&
    FirebaseRemoteConfig.isFeatureEnabled(
      FEATURE_CONFIG.AUTO_CHART_SYNC_CONFIG,
    );

  return (
    <React.Fragment>
      {/* TODO: Uncomment when BarNotifications are supported */}
      {/* {barNotification ? (
        <BarNotification
          notification={barNotification}
          handleAction={handleAction}
          index={barNotificationIndex + 1}
          totalCount={barNotifications.length}
          next={() => {
            next('bar');
          }}
          previous={() => {
            previous('bar');
          }}
        />
      ) : (
        <React.Fragment />
      )} */}
      {modalNotification ? (
        <ModalNotification
          notification={modalNotification}
          handleAction={handleAction}
          totalCount={modalNotifications.length}
          index={modalNotificationIndex + 1}
          next={() => {
            next('modal');
          }}
          previous={() => {
            previous('modal');
          }}
        />
      ) : (
        <React.Fragment />
      )}
    </React.Fragment>
  );
};

export default observer(NotificationCenterContainer);
